import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactForm from './components/ContactForm';
import ProjectsGallery from './components/ProjectsGallery';
import LocationMap from './components/LocationMap';
import SupportForm from './components/SupportForm';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import BookingCalendar from '../../components/feature/BookingCalendar';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaLocalBusiness = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: 'JyA Fotovoltaica',
  description: 'Instalación de paneles solares y sistemas fotovoltaicos para hogares y empresas en Navalcarnero, Madrid. Ahorra hasta 80% en tu factura eléctrica.',
  url: SITE_URL,
  telephone: '+34680254236',
  email: 'jyafotovoltaica@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Calle Eras 17',
    addressLocality: 'Navalcarnero',
    addressRegion: 'Madrid',
    postalCode: '28600',
    addressCountry: 'ES',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 40.2833,
    longitude: -3.9333,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  sameAs: [
    'https://www.linkedin.com',
    'https://www.instagram.com',
    'https://www.youtube.com',
  ],
  priceRange: '€€',
  currenciesAccepted: 'EUR',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer',
  areaServed: {
    '@type': 'State',
    name: 'Madrid',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Soluciones Fotovoltaicas',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Instalaciones Residenciales' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Proyectos Industriales' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Autoconsumo Empresarial' } },
    ],
  },
};

const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'JyA Fotovoltaica',
  description: 'Instalación de paneles solares y sistemas fotovoltaicos en Navalcarnero, Madrid.',
  inLanguage: 'es-ES',
  publisher: {
    '@id': `${SITE_URL}/#business`,
  },
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '¿Cuánto puedo ahorrar con paneles solares en Madrid?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Con una instalación fotovoltaica en Madrid puedes ahorrar hasta un 80% en tu factura eléctrica, dependiendo del tamaño del sistema y tu consumo habitual.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Cuánto tiempo tarda la instalación de paneles solares?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Una instalación residencial estándar se completa en 1-2 días. Los proyectos industriales pueden requerir entre 1 y 2 semanas según la envergadura.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Qué garantía ofrecen en las instalaciones fotovoltaicas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Ofrecemos garantía de 25 años en los paneles solares y 10 años en los inversores, además de garantía de instalación y servicio postventa.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Dónde está ubicada JyA Fotovoltaica?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Estamos en Calle Eras 17, 28600 Navalcarnero, Madrid. Atendemos en horario de lunes a viernes de 9:00 a 18:00.',
      },
    },
  ],
};

const schemaWebPage = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'JyA Fotovoltaica Navalcarnero - Instalación Paneles Solares Madrid',
  description: 'JyA Fotovoltaica en Navalcarnero, Madrid. Instalación paneles solares y sistemas fotovoltaicos para hogares y empresas. Ahorra hasta 80% en tu factura eléctrica con energía solar autoconsumo. Presupuesto gratuito en 24h.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#business` },
  dateModified: '2026-04-07',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: SITE_URL,
      },
    ],
  },
};

const schemaService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service`,
  name: 'Instalación de Paneles Solares Fotovoltaicos',
  description: 'Instalación profesional de paneles solares y sistemas fotovoltaicos para hogares, empresas e industrias en Navalcarnero y toda la Comunidad de Madrid. Energía solar autoconsumo con presupuesto gratuito.',
  provider: { '@id': `${SITE_URL}/#business` },
  areaServed: {
    '@type': 'State',
    name: 'Madrid',
    '@id': 'https://www.wikidata.org/wiki/Q5756',
  },
  serviceType: 'Instalación Fotovoltaica',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
    description: 'Presupuesto gratuito sin compromiso en 24 horas',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Servicios Fotovoltaicos',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Instalaciones Residenciales',
          description: 'Sistemas solares para hogares con máxima eficiencia y ahorro garantizado en Madrid.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Proyectos Industriales',
          description: 'Soluciones fotovoltaicas a gran escala para empresas y naves industriales en Madrid.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Autoconsumo Empresarial',
          description: 'Reduce costes operativos con energía solar autoconsumo para tu negocio en Madrid.',
        },
      },
    ],
  },
};

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Inject Schema.org JSON-LD scripts
    const schemas = [schemaLocalBusiness, schemaWebSite, schemaFAQ, schemaWebPage, schemaService];
    const injected: HTMLScriptElement[] = [];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.setAttribute('data-schema', 'true');
      document.head.appendChild(script);
      injected.push(script);
    });

    return () => {
      injected.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      await fetch('https://readdy.ai/api/form/d6dnrubv53dtlm7j8ofg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setEmail('');
    } catch {
      // silent fail
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/d37e4615f28c8b6905f3494c85f65781/c4508ab83522ccc97549a11b398889f7.jpeg" 
                alt="Logo Empresa Solar" 
                className="h-40 w-auto object-contain"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#soluciones" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Soluciones</a>
              <a href="#productos" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Productos</a>
              <a href="#proyectos" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Proyectos</a>
              <a href="#nosotros" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Nosotros</a>
              <a href="#presupuesto" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Contacto</a>
              <a href="#agendar-visita" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Agendar Visita</a>
              <a href="#soporte" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Soporte</a>
              <a href="#ubicacion" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#FFB800]' : 'text-white hover:text-[#FFB800]'}`}>Ubicación</a>
            </div>
            <a href="#presupuesto" className="bg-[#FFB800] text-gray-900 px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#FF6B35] transition-all duration-300 whitespace-nowrap cursor-pointer">
              Solicitar Presupuesto
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/1bab853c3f2a51ba42efb29ecfda79fb.jpg" 
            alt="Instalación Solar" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                JyA Fotovoltaica
              </h1>
              <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed">
                Soluciones fotovoltaicas integrales para hogares y empresas. Ahorra hasta un 80% en tu factura eléctrica con energía limpia y sostenible.
              </p>
              <div className="flex flex-wrap gap-4 mb-12">
                <a href="#presupuesto" className="bg-[#FF6B35] text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-[#FFB800] transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Calcular Ahorro
                </a>
                <a href="#proyectos" className="border-2 border-white text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Ver Proyectos
                </a>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-[#FFB800] mb-1">+5.000</div>
                  <div className="text-sm text-gray-300">Instalaciones</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-[#FFB800] mb-1">Nº1</div>
                  <div className="text-sm text-gray-300">en España</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-[#FFB800] mb-1">10 años</div>
                  <div className="text-sm text-gray-300">Experiencia</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="soluciones" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Soluciones Fotovoltaicas Integrales
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Diseñamos e instalamos sistemas solares personalizados para cada necesidad
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative overflow-hidden rounded-3xl h-96 cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=residential%20solar%20panel%20installation%20on%20modern%20house%20roof%2C%20clean%20simple%20background%2C%20professional%20photography%20showing%20home%20photovoltaic%20system%20with%20blue%20sky%2C%20sustainable%20energy%20for%20families%20in%20Spain&width=600&height=800&seq=feature-residential-1&orientation=portrait" 
                alt="Instalaciones Residenciales" 
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 bg-[#FFB800] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-home-4-line text-2xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Instalaciones Residenciales</h3>
                <p className="text-gray-200 text-sm">Sistemas solares para hogares con máxima eficiencia y ahorro garantizado</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl h-96 cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=industrial%20solar%20panel%20farm%20installation%2C%20large%20scale%20photovoltaic%20system%20for%20factories%20and%20warehouses%2C%20clean%20simple%20background%2C%20professional%20photography%20showing%20commercial%20renewable%20energy%20in%20Spain&width=600&height=800&seq=feature-industrial-1&orientation=portrait" 
                alt="Proyectos Industriales" 
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 bg-[#FFB800] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-2-line text-2xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Proyectos Industriales</h3>
                <p className="text-gray-200 text-sm">Soluciones a gran escala para empresas y naves industriales</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl h-96 cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=commercial%20building%20with%20solar%20panels%2C%20business%20energy%20self%20consumption%20system%2C%20clean%20simple%20background%2C%20professional%20photography%20of%20corporate%20photovoltaic%20installation%20in%20Spain%2C%20modern%20office%20building&width=600&height=800&seq=feature-commercial-1&orientation=portrait" 
                alt="Autoconsumo Empresarial" 
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 bg-[#FFB800] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-store-2-line text-2xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Autoconsumo Empresarial</h3>
                <p className="text-gray-200 text-sm">Reduce costes operativos con energía solar para tu negocio</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20solar%20installation%20team%20working%20on%20photovoltaic%20panels%2C%20certified%20technicians%20installing%20solar%20energy%20system%2C%20clean%20simple%20background%2C%20teamwork%20in%20renewable%20energy%20industry%20in%20Spain%2C%20high%20quality%20professional%20photography&width=800&height=900&seq=about-team-1&orientation=portrait" 
                alt="Equipo Profesional" 
                className="w-full h-full object-cover object-top rounded-3xl"
              />
              <div className="absolute top-6 left-6 flex flex-col gap-3">
                <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-gray-900">
                  Certificado ISO 9001
                </div>
                <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-gray-900">
                  Garantía 30 años
                </div>
                <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-gray-900">
                  Instaladores Certificados
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pioneros en Energía Renovable
              </h2>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">Innovación</span>
                <span className="text-gray-600 px-4 py-2 text-sm font-medium">Calidad</span>
                <span className="text-gray-600 px-4 py-2 text-sm font-medium">Sostenibilidad</span>
              </div>

              <div className="bg-[#0A2E1F] rounded-3xl p-8 md:p-10">
                <div className="bg-[#FFB800] text-gray-900 px-4 py-1.5 rounded-full text-xs font-bold inline-block mb-6">
                  Desde 2005
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">Nuestra Trayectoria</h3>
                    <p className="text-gray-300 text-sm leading-relaxed mb-6">
                      Con más de dos décadas de experiencia, nos hemos consolidado como la empresa líder en instalaciones fotovoltaicas en España. Nuestro compromiso con la excelencia y la innovación nos ha permitido realizar más de 5.000 instalaciones exitosas en todo el territorio nacional.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Trabajamos con las mejores marcas del mercado y contamos con un equipo de más de 100 profesionales certificados que garantizan instalaciones de máxima calidad y rendimiento.
                    </p>
                  </div>
                  <div className="flex items-center justify-center">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Spain%20map%20with%20solar%20energy%20growth%20chart%2C%20renewable%20energy%20statistics%20visualization%2C%20clean%20simple%20background%2C%20professional%20infographic%20showing%20photovoltaic%20expansion%20across%20Spanish%20regions%2C%20modern%20data%20visualization&width=400&height=400&seq=about-map-1&orientation=squarish" 
                      alt="Expansión Nacional" 
                      className="w-full h-64 object-cover object-top rounded-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section id="proyectos" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-[#0A2E1F] rounded-[3rem] overflow-hidden">
            <div className="grid md:grid-cols-3 gap-0">
              <div className="relative -ml-6 md:-ml-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=satisfied%20Spanish%20business%20owner%20standing%20in%20front%20of%20solar%20panels%2C%20professional%20portrait%20of%20happy%20client%20with%20photovoltaic%20installation%2C%20clean%20simple%20background%2C%20confident%20entrepreneur%20with%20renewable%20energy%20system%2C%20natural%20lighting&width=400&height=600&seq=testimonial-client-1&orientation=portrait" 
                  alt="Cliente Satisfecho" 
                  className="w-full h-full object-cover object-top rounded-3xl"
                />
              </div>
              
              <div className="md:col-span-2 p-8 md:p-12 flex flex-col justify-center">
                <div className="text-6xl text-[#F5F1E8] mb-6 font-serif">"</div>
                <p className="text-white text-xl md:text-2xl leading-relaxed mb-8">
                  La instalación superó todas nuestras expectativas. En solo 6 meses hemos reducido nuestra factura eléctrica en un 75%. El equipo fue profesional, rápido y el seguimiento post-instalación es excepcional. Sin duda, la mejor inversión que hemos hecho para nuestra empresa.
                </p>
                <div className="mb-8">
                  <div className="text-white font-semibold text-lg mb-1">Carlos Martínez</div>
                  <div className="text-gray-400 text-sm">Director General, Industrias Martínez - Madrid</div>
                </div>
                <button className="bg-[#FFB8B8] hover:bg-[#FFB800] text-gray-900 px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 self-start whitespace-nowrap cursor-pointer">
                  Ver Más Casos de Éxito
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <ProjectsGallery />

      {/* Products Section */}
      <section id="productos" className="py-24 bg-[#0A2E1F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <button className="border-2 border-white/30 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-white hover:text-gray-900 transition-all duration-300 mb-4 md:mb-0 whitespace-nowrap cursor-pointer">
              Catálogo Completo
            </button>
            <h2 className="text-4xl md:text-5xl font-bold text-white md:w-2/3 leading-tight">
              Tecnología Solar de Última Generación
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group cursor-pointer hover:transform hover:scale-105 transition-all duration-300" data-product-shop>
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=high%20efficiency%20monocrystalline%20solar%20panel%20close%20up%2C%20premium%20photovoltaic%20module%20with%20clean%20simple%20white%20background%2C%20professional%20product%20photography%20of%20black%20solar%20cell%20technology%2C%20detailed%20view%20of%20modern%20solar%20panel%20surface&width=500&height=500&seq=product-panel-1&orientation=squarish" 
                  alt="Paneles Monocrystalline Premium" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-[#0A2E1F] p-6">
                <h3 className="text-white font-semibold text-lg mb-2">Paneles Monocrystalline Premium</h3>
                <p className="text-gray-400 text-sm">Eficiencia hasta 22.5% - 450W</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group cursor-pointer hover:transform hover:scale-105 transition-all duration-300" data-product-shop>
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20hybrid%20solar%20inverter%20device%2C%20professional%20product%20photography%20of%20photovoltaic%20power%20converter%20with%20clean%20simple%20white%20background%2C%20high%20tech%20solar%20energy%20equipment%2C%20sleek%20black%20inverter%20design&width=500&height=500&seq=product-inverter-1&orientation=squarish" 
                  alt="Inversores Híbridos" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-[#0A2E1F] p-6">
                <h3 className="text-white font-semibold text-lg mb-2">Inversores Híbridos</h3>
                <p className="text-gray-400 text-sm">Potencia 5-10kW - Monitorización WiFi</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group cursor-pointer hover:transform hover:scale-105 transition-all duration-300" data-product-shop>
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=lithium%20battery%20storage%20system%20for%20solar%20energy%2C%20professional%20product%20photography%20of%20home%20energy%20storage%20unit%20with%20clean%20simple%20white%20background%2C%20modern%20solar%20battery%20pack%2C%20sleek%20design%20power%20storage%20device&width=500&height=500&seq=product-battery-1&orientation=squarish" 
                  alt="Baterías de Almacenamiento" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-[#0A2E1F] p-6">
                <h3 className="text-white font-semibold text-lg mb-2">Baterías de Almacenamiento</h3>
                <p className="text-gray-400 text-sm">Capacidad 5-15kWh - Litio LFP</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group cursor-pointer hover:transform hover:scale-105 transition-all duration-300" data-product-shop>
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=solar%20monitoring%20system%20display%20screen%2C%20professional%20product%20photography%20of%20photovoltaic%20energy%20management%20device%20with%20clean%20simple%20white%20background%2C%20digital%20solar%20panel%20monitoring%20interface%2C%20modern%20smart%20energy%20control%20panel&width=500&height=500&seq=product-monitor-1&orientation=squarish" 
                  alt="Sistemas de Monitorización" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="bg-[#0A2E1F] p-6">
                <h3 className="text-white font-semibold text-lg mb-2">Sistemas de Monitorización</h3>
                <p className="text-gray-400 text-sm">Control en tiempo real - App móvil</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comienza Tu Transición Energética Hoy
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Presupuesto personalizado sin compromiso en 24 horas
          </p>
          <a href="#presupuesto" className="bg-[#0A2E1F] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 inline-flex items-center gap-3 whitespace-nowrap cursor-pointer">
            Solicitar Presupuesto Gratuito
            <i className="ri-arrow-right-line text-xl"></i>
          </a>

          <div className="mt-20 pt-12 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-8">Marcas con las que trabajamos</p>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
              <div className="text-2xl font-bold text-gray-400">LONGI</div>
              <div className="text-2xl font-bold text-gray-400">HUAWEI</div>
              <div className="text-2xl font-bold text-gray-400">JINKO</div>
              <div className="text-2xl font-bold text-gray-400">FRONIUS</div>
              <div className="text-2xl font-bold text-gray-400">SMA</div>
              <div className="text-2xl font-bold text-gray-400">BYD</div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Calendar Section */}
      <section id="agendar-visita" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left info */}
            <div>
              <span className="inline-block bg-[#FFB800]/15 text-[#0A2E1F] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
                Visita gratuita
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Agenda Tu Visita <br />de Presupuesto
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-10 max-w-md">
                Reserva una visita técnica gratuita en tu domicilio o empresa. Nuestros expertos evaluarán tu instalación y te darán un presupuesto personalizado sin compromiso.
              </p>

              <div className="space-y-5 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FFB800] flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-gray-900 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Visita en tu ubicación</h4>
                    <p className="text-gray-500 text-sm">Nos desplazamos a tu domicilio o empresa en Madrid y alrededores.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FFB800] flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gray-900 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Estudio técnico completo</h4>
                    <p className="text-gray-500 text-sm">Análisis de consumo, orientación del tejado y dimensionado del sistema.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FFB800] flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-gray-900 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Presupuesto detallado</h4>
                    <p className="text-gray-500 text-sm">Recibirás un presupuesto completo con retorno de inversión estimado.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#0A2E1F]/5 rounded-2xl p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Horario de visitas</p>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-calendar-line text-[#FF6B35]"></i>
                  <span>Lunes a Viernes</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700 mt-2">
                  <i className="ri-time-line text-[#FF6B35]"></i>
                  <span>09:00 – 18:00</span>
                </div>
              </div>
            </div>

            {/* Right: Calendar */}
            <div className="bg-white border border-gray-100 rounded-3xl p-8 md:p-10" style={{ boxShadow: '0 2px 24px 0 rgba(10,46,31,0.07)' }}>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Selecciona fecha y hora</h3>
              <p className="text-gray-400 text-sm mb-6">Elige el día y horario que mejor te venga</p>
              <BookingCalendar />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      {/* Support Form */}
      <SupportForm />

      {/* Location Map */}
      <LocationMap />

      {/* Footer */}
      <footer id="contacto" className="bg-[#0A2E1F] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/d37e4615f28c8b6905f3494c85f65781/c4508ab83522ccc97549a11b398889f7.jpeg" 
                alt="Logo" 
                className="h-32 w-auto object-contain mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Líderes en soluciones fotovoltaicas en España. Más de 20 años transformando la energía del futuro.
              </p>
            </div>

            <div className="flex items-center gap-6">
              <a href="tel:+34680254236" className="flex items-center gap-2 text-[#0A2E1F] font-semibold text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-[#0A2E1F] flex items-center justify-center">
                  <i className="ri-phone-line text-white text-lg"></i>
                </div>
                680254236
              </a>
              <a href="mailto:jyafotovoltaica@gmail.com" className="flex items-center gap-2 text-[#0A2E1F] font-semibold text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-[#0A2E1F] flex items-center justify-center">
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                jyafotovoltaica@gmail.com
              </a>
            </div>

            <div>
              <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-4">NEWSLETTER</h4>
              <form onSubmit={handleNewsletterSubmit} id="newsletter-form" data-readdy-form className="mb-4">
                <div className="relative">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Tu email" 
                    className="w-full bg-transparent border-b border-gray-600 py-2 pr-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#FFB800] transition-colors"
                    required
                  />
                  <button type="submit" className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer">
                    <i className="ri-arrow-right-line text-[#FFB800]"></i>
                  </button>
                </div>
              </form>
              <p className="text-xs text-gray-500">
                Al suscribirte aceptas nuestra <a href="#" className="underline hover:text-[#FFB800]">política de privacidad</a>
              </p>
            </div>

            <div>
              <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-4">NAVEGACIÓN</h4>
              <ul className="space-y-3">
                <li><a href="#productos" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">Productos</a></li>
                <li><a href="#soluciones" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">Servicios</a></li>
                <li><a href="#proyectos" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">Blog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-4">SÍGUENOS</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">LinkedIn</a></li>
                <li><a href="#" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">Instagram</a></li>
                <li><a href="#" className="text-white hover:text-[#FFB800] transition-colors text-sm cursor-pointer">YouTube</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="text-center">
              <h2 className="text-5xl md:text-7xl font-bold text-[#F5F1E8] mb-6 tracking-tight">
                ENERGÍA SOLAR
              </h2>
              <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
                <p>© 2024 Energía Solar España. Todos los derechos reservados.</p>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#FFB800] transition-colors cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
}
